import * as THREE from 'three';
import { SALT_HALF_X, SALT_HALF_Y, SALT_PITCH_X, SALT_PITCH_Y, saltGround, sluiceFlow, type BattleState, type MapDef, type SaltRules } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The salt flats (docs/sendas/paladin.md, chapter 2), for the paladin's path: a white coast flat to the horizon, square
 * pools of still water mirroring a white sky, raised dykes of salt between them, fog off the sea. Never the warrior's
 * snow (blue-white, soft, in drifts) nor chapter 1's black glass: here the ground is a dry crust, cracked in plates,
 * that glitters; the water is a mirror in hard-edged squares; the light is flat and pearly.
 *
 * - `saltFlats` (the fog's and the gates' acts, the scenes): the pools in rows to the horizon, the board's own drawn at
 *   their marks (the water's level a mirror plane in a basin; dry, a white crust; the altar's, under water until the
 *   gates drain it, its eye opening at the bottom); the gate house in the middle (pale stone, slate roof, its door to
 *   the coast); the four houses of the salt by the shore, salt heaped in white cones, rakes; the wooden sluices at the
 *   gates (the gate lifts as it is turned, a white rush of water runs through it and the levels step down as it runs).
 * - `saltFog` (the test): the same at dusk under thicker fog; the ways' dry pools; Marit's salt circles (bright on a
 *   dyke, greying and shrinking as they melt in the mud); the old stalker's mark on Marit, red.
 * - `saltNight` (the campfire): the same at night, the gate house's windows lit.
 *
 * Footprints: what treads on salt leaves dark prints that fade (the tracks the test is read by); drawn here from the
 * nightmares' places, a few a second (render only, nothing in the sim).
 *
 * Draw calls: the dressing (one merged mesh), the pool beds and the rims (two instanced), the water (one instanced),
 * the ground fog (one), the prints, circles and marks (one instanced), the sluice runs (one instanced), the glow layer,
 * the sky dome. No lights of its own: the board's are repainted (HOURS). docs/RENDIMIENTO.md, «Reglas para lo nuevo».
 */

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WHITE = new THREE.Color('#ffffff');
const LAMP = new THREE.Color('#ffd9b0');
const SALT = ['#eef1f3', '#e4e8eb', '#f6f7f8'];
const STONE = ['#c3c7cd', '#b4b9c0', '#d0d3d8'];
const SLATE = '#3d434d';

export const SALT_PLACES = new Set(['saltFlats', 'saltFog', 'saltNight']);

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The flats' light: pearly and flat by day, fog the colour of the sky; dusk cold lilac-grey; night deep blue. */
const HOURS: Record<Hour, { bg: string; fog: string; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; mist: number }> = {
  day: { bg: '#d6dce2', fog: '#dce1e6', hemi: ['#eef2f6', '#4a4f58', 1.85], key: ['#fffaf4', 2.4], rim: ['#b4c6e6', 1.2], sky: ['#9aa9bb', '#eef1f4', '#ffffff'], mist: 1 },
  dawn: { bg: '#d9dfe6', fog: '#dde3e9', hemi: ['#eef2f7', '#4f545e', 2.3], key: ['#fff4ee', 2.8], rim: ['#b8c8ec', 1.4], sky: ['#8c9cb2', '#eef1f5', '#fff8f2'], mist: 1 },
  dusk: { bg: '#7d8794', fog: '#8a939f', hemi: ['#b3bccb', '#2a2d35', 1.35], key: ['#d6dcec', 1.1], rim: ['#93a9dc', 1.3], sky: ['#3d4558', '#8e97a5', '#d6dbe8'], mist: 1.15 },
  night: { bg: '#0a0e16', fog: '#111722', hemi: ['#6577a0', '#0c0d12', 0.7], key: ['#aabbe4', 0.5], rim: ['#7a96ff', 1.2], sky: ['#03050a', '#121a28', '#9fb2e2'], mist: 0.55 },
};

/**
 * The salt crust: the board's material with plates cracked in it (a cell pattern, its seams a shade darker), a fine
 * grain, and a glitter where the light catches it at a grazing look.
 */
const crustMaterial = (src: THREE.MeshStandardMaterial, u: { uTime: THREE.IUniform<number>; uGlint: THREE.IUniform<number> }): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.roughness = 0.82;
  m.metalness = 0;
  m.flatShading = false;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSaW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSaW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uTime;
uniform float uGlint;
varying vec3 vSaW;
vec2 saH(vec2 p) { p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3))); return fract(sin(p) * 43758.5453); }
// Distance to the nearest seam between cells (the crust's plates).
float saSeam(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  float d1 = 8.0, d2 = 8.0;
  for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
    vec2 g = vec2(float(x), float(y));
    vec2 o = saH(i + g);
    float d = length(g + o - f);
    if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
  }
  return d2 - d1;
}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  vec2 q = mod(vSaW.xz, 400.0);
  float seam = saSeam(q * 1.9);
  float fine = saSeam(q * 5.3);
  float crack = 1.0 - smoothstep(0.0, 0.05, seam);
  float hair = 1.0 - smoothstep(0.0, 0.035, fine);
  diffuseColor.rgb *= 1.0 - crack * 0.16 - hair * 0.06;
  // The plates a little raised at their rims: lighter just inside the seam.
  diffuseColor.rgb *= 1.0 + smoothstep(0.06, 0.16, seam) * (1.0 - smoothstep(0.16, 0.3, seam)) * 0.05;
  // Raked rows in patches (the salt folk's work), and a cold grey bloom of damp in the low spots.
  float patch_ = step(0.6, saH(floor(vSaW.xz * 0.14)).x);
  float rows = smoothstep(0.5, 1.0, sin(vSaW.z * 34.0 + saH(floor(vSaW.xz * 0.14)).y * 6.0));
  diffuseColor.rgb *= 1.0 - rows * patch_ * 0.08;
  float damp = smoothstep(0.55, 0.9, saH(floor(vSaW.xz * 0.5)).x) * 0.05;
  diffuseColor.rgb *= 1.0 - damp;
}`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
{
  vec2 g = floor(vSaW.xz * 9.0);
  float r = saH(g).x;
  float tw = step(0.985, r) * (0.5 + 0.5 * sin(uTime * (1.5 + r * 4.0) + r * 60.0));
  float fr = pow(1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0), 2.0);
  totalEmissiveRadiance += vec3(tw * (0.35 + fr) * uGlint);
}`,
      );
  };
  m.customProgramCacheKey = () => 'somnhold-salt-crust';
  m.needsUpdate = true;
  return m;
};

/** The pools' water: a mirror of the white sky (more at a grazing look), slow rings on it, a bright line at its rim. */
const waterMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: { time: { value: 0 }, sky: { value: new THREE.Color('#eef1f4') }, deep: { value: new THREE.Color('#2b4152') }, night: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      varying vec2 vUv;
      varying vec3 vTint;
      varying float vSeed;
      varying vec2 vSize;
      void main() {
        vUv = uv;
        vTint = instanceColor;
        vec2 c = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz;
        vSeed = fract(sin(dot(floor(c * 3.0), vec2(12.9898, 78.233))) * 43758.5453);
        vSize = vec2(length(instanceMatrix[0].xyz), length(instanceMatrix[2].xyz));
        vec4 w = modelMatrix * instanceMatrix * vec4(position, 1.0);
        vW = w.xyz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 sky;
      uniform vec3 deep;
      uniform float night;
      varying vec3 vW;
      varying vec2 vUv;
      varying vec3 vTint;
      varying float vSeed;
      varying vec2 vSize;
      float wh(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float wn(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(wh(i), wh(i + vec2(1, 0)), u.x), mix(wh(i + vec2(0, 1)), wh(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        // The pond's own shore: a rounded, wobbling outline (in world units, so every size wobbles alike), crusted white.
        vec2 q = (vUv - 0.5) * vSize;
        vec2 hs = vSize * 0.5;
        float rr = min(hs.x, hs.y) * 0.35;
        vec2 dd = abs(q) - (hs - rr);
        float sd = length(max(dd, 0.0)) + min(max(dd.x, dd.y), 0.0) - rr;
        sd += (wn(vW.xz * 1.3 + vSeed * 17.0) - 0.5) * 0.32 + (wn(vW.xz * 4.0) - 0.5) * 0.08;
        if (sd > 0.0) discard;
        float shore = 1.0 - smoothstep(-0.16, -0.02, sd);
        vec3 v = normalize(cameraPosition - vW);
        float fr = pow(1.0 - clamp(v.y, 0.0, 1.0), 1.6);
        float rip = sin(vW.x * 2.1 - vW.z * 1.3 + time * 0.9) * sin(vW.z * 1.7 + vW.x * 0.6 - time * 0.7) * 0.5 + 0.5;
        float swell = sin(vW.x * 0.7 + vW.z * 0.4 + time * 0.5) * 0.5 + 0.5;
        // A mirror: the deep where one looks down into it, the white sky reflected toward its far side (the uv's
        // far edge), a glint where the light lies on it.
        float far_ = smoothstep(-0.2, 1.0, fr);
        float sky_ = smoothstep(0.15, 1.0, vUv.y) * 0.55 + far_ * 0.35;
        // Its tint by salinity: the brine's colour seen into, the sky over it at a grazing look.
        vec3 tint = vTint;
        vec3 c = mix(mix(deep, tint * 0.75, 0.75), mix(sky * 0.95, tint, 0.35), clamp(sky_, 0.0, 0.8));
        float streak = exp(-pow((vUv.x - vUv.y * 0.6) * 5.0 - 1.4, 2.0) * 3.0);
        c += vec3(0.16) * streak * (0.6 + 0.4 * swell);
        c += vec3(0.035) * rip;
        // The salt crusted at its shore, white, in a broken band.
        c = mix(c, vec3(0.97, 0.98, 0.99), (1.0 - shore) * (0.55 + 0.45 * wn(vW.xz * 9.0)));
        c *= mix(1.0, 0.35, night);
        gl_FragColor = vec4(c, 0.94);
        #include <colorspace_fragment>
      }`,
  });

/** Fog lying on the flats: drifting banks, thin over the board's middle, thick past its rim (the sea's side most). */
const mistMat = (layer = 0): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    fog: false,
    uniforms: { time: { value: 0 }, color: { value: new THREE.Color('#e9edf0') }, thick: { value: 0.5 }, board: { value: 8.6 * S } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 color;
      uniform float thick;
      uniform float board;
      varying vec3 vW;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        // Banks of fog drifting in off the sea (this layer's own size and pace): rolls of it with clear lanes between,
        // each layer higher, thinner and slower, so it reads thick from the game's slanted eye.
        vec2 p = vW.xz * ${(0.075 - layer * 0.018).toFixed(3)} + vec2(time * ${(0.035 - layer * 0.01).toFixed(3)}, time * 0.012 + ${(layer * 3.7).toFixed(1)});
        float f = n(p) * 0.55 + n(p * 2.3 - vec2(time * 0.05, 0.0)) * 0.3 + n(p * 5.1 + 9.0) * 0.15;
        float bank = smoothstep(0.3, 0.75, n(vW.xz * 0.025 + vec2(time * 0.02, ${(layer * 5.3).toFixed(1)})));
        float d = length(vW.xz);
        float out_ = smoothstep(board * 0.75, board * 1.35, d);
        vec3 col = mix(color, color * vec3(0.84, 0.87, 0.9), smoothstep(0.45, 0.85, f) * bank);
        float a = mix(smoothstep(0.32, 0.8, f) * thick * (0.35 + 0.9 * bank) * ${(1 - layer * 0.28).toFixed(2)}, ${(0.92 - layer * 0.3).toFixed(2)}, out_);
        // (Seen edge-on, a sheet is a hard line across the scene: low cameras see through it, the haze does the rest.)
        a *= smoothstep(0.06, 0.38, normalize(cameraPosition - vW).y);
        gl_FragColor = vec4(col, clamp(a, 0.0, 0.95));
        #include <colorspace_fragment>
      }`,
  });

/** Where a pool is drawn: its middle (world), half its size, and its marks' look (shown, eased toward the sim's). */
interface PoolView {
  /** Its ground (the flats' one height).*/
  g: number;
  x: number;
  z: number;
  hx: number;
  hz: number;
  /** Index into the board's pools (−1: one of the flats' beyond it, always full). */
  board: number;
  shown: number;
  /** Its water's tint by how salt it has turned (TINTS). */
  tint: number;
  /** How much of its basin the water's outline fills, across and along (the board's own ponds vary a little). */
  fx?: number;
  fz?: number;
}

/**
 * The ponds' water by salinity (never beige or gold): fresh from the sea a pale teal, then milky as the brine thickens,
 * and a cold rose where it is ready to crystallise.
 */
const TINTS = [new THREE.Color('#6fa3a6'), new THREE.Color('#c9d3d8'), new THREE.Color('#c39cb0'), new THREE.Color('#8fb7b8')];

export class SaltLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private crustU = { uTime: { value: 0 }, uGlint: { value: 1 } };
  private pools: PoolView[] = [];
  private beds: THREE.InstancedMesh | null = null;
  private stakes: { mesh: THREE.InstancedMesh; pools: number[] } | null = null;
  private water: THREE.InstancedMesh | null = null;
  private waterMat: THREE.ShaderMaterial | null = null;
  private mist: THREE.Mesh | null = null;
  private mistU: THREE.ShaderMaterial | null = null;
  /** The decals: prints, salt circles, the leap's mark, the throw. */
  private decals: THREE.InstancedMesh | null = null;
  private runs: THREE.InstancedMesh | null = null;
  private gates: { at: THREE.Vector3; plank: THREE.Mesh; yaw: number; up: number }[] = [];
  private prints: { x: number; z: number; yaw: number; t: number }[] = [];
  private printLast = new Map<number, { x: number; y: number }>();
  private eye: { at: THREE.Vector3; mesh: THREE.Mesh; mat: THREE.MeshStandardMaterial; open: number } | null = null;
  private lamps: THREE.Vector3[] = [];
  /** Where the houses' chimneys smoke (the smoke: one instanced mesh of drifting puffs). */
  private smokeAt: THREE.Vector3[] = [];
  private smoke: THREE.Mesh | null = null;
  private mists: THREE.ShaderMaterial[] = [];
  private fire: THREE.Vector3 | null = null;
  private hour: Hour = 'day';
  private now = 0;
  private lastTick = -1;
  /** A scene's flats: the altar drained and its eye open (the close). */
  private lit = false;
  /** The fog's act: the banks thick over the flats. */
  private foggy = false;
  private flat = 0;
  readonly anchors: { gates: THREE.Vector3[]; altar: THREE.Vector3 | null; door: THREE.Vector3 | null } = { gates: [], altar: null, door: null };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `salt-${place}`;
    // One height for the whole flats (the board laid flat to it in apply(), the world beyond too).
    this.flat = ctx.ground(0, 0);
    // (A scene's flats with the altar drained: the close.)
    this.lit = !!s.senda?.salt && s.senda.salt.dried >= 0 && s.rules?.senda?.salt?.kind !== 'fog';
    const r = s.rules?.senda?.salt;
    this.foggy = r?.kind === 'fog';
    const kit = new Kit();
    if (r) this.buildFlats(kit, r);
    this.buildShore(kit);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.78, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'salt-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.buildDecals();
    this.buildSmoke();
    this.group.add(this.glow.mesh);
  }

  /** The chimneys' smoke: soft puffs rising and leaning off with the wind, a few a chimney (one instanced mesh). */
  private buildSmoke(): void {
    const per = 7;
    const n = this.smokeAt.length * per;
    if (!n) return;
    const q = new THREE.PlaneGeometry(1, 1);
    const base = new Float32Array(n * 4);
    this.smokeAt.forEach((p, i) => {
      for (let k = 0; k < per; k++) {
        const j = (i * per + k) * 4;
        base[j] = p.x;
        base[j + 1] = p.y;
        base[j + 2] = p.z;
        base[j + 3] = k / per + i * 0.13;
      }
    });
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = q.index;
    geo.setAttribute('position', q.getAttribute('position'));
    geo.setAttribute('uv', q.getAttribute('uv'));
    geo.setAttribute('aBase', new THREE.InstancedBufferAttribute(base, 4));
    geo.instanceCount = n;
    q.dispose();
    this.geos.push(geo);
    const m = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { time: { value: 0 }, color: { value: new THREE.Color('#d6dbe1') } },
      vertexShader: /* glsl */ `
        uniform float time;
        attribute vec4 aBase;
        varying vec2 vUv;
        varying float vT;
        void main() {
          vUv = uv;
          float t = fract(time * 0.07 + aBase.w);
          vT = t;
          vec3 c = aBase.xyz + vec3(t * 1.6 + sin(t * 6.0 + aBase.w * 20.0) * 0.12, t * 2.4, -t * 0.5);
          float size = 0.25 + t * 1.1;
          vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
          vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
          vec3 w = c + (right * position.x + up * position.y) * size;
          gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 color;
        varying vec2 vUv;
        varying float vT;
        void main() {
          float r = length(vUv - 0.5) * 2.0;
          float a = (1.0 - smoothstep(0.2, 1.0, r)) * smoothstep(0.0, 0.12, vT) * (1.0 - vT) * 0.42;
          gl_FragColor = vec4(color, a);
          #include <colorspace_fragment>
        }`,
    });
    this.mats.push(m);
    const mesh = new THREE.Mesh(geo, m);
    mesh.frustumCulled = false;
    mesh.renderOrder = 8;
    mesh.name = 'salt-smoke';
    this.smoke = mesh;
    this.group.add(mesh);
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, hour: Hour): void {
    this.terrain = terrain;
    // A salt flat is flat: the board's tiles (its rim of rock too, and the house's cells) laid at one height, so its
    // edge never shows the hex zigzag; the ground the pieces stand on read from the same.
    const center = this.flat;
    {
      const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
      terrain.mesh.updateMatrixWorld(true);
      const inv = terrain.mesh.matrixWorld.clone().invert();
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        v.y = center;
        v.applyMatrix4(inv);
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      pos.needsUpdate = true;
      terrain.heights.fill(center);
    }
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const c = new THREE.Color();
    const p = new THREE.Vector3();
    const tint = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!col || !pos) return;
      mesh.updateMatrixWorld(true);
      for (let i = 0; i < pos.count; i++) {
        p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        // One crust, board and beyond alike (no seam where the board ends): long soft swells of grey in the white.
        const k = 0.5 + 0.25 * Math.sin(p.x * 0.21 + p.z * 0.13) + 0.25 * Math.sin(p.z * 0.31 - p.x * 0.09);
        c.set('#c9cfd5').lerp(new THREE.Color('#e9ecef'), k);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    };
    tint(terrain.mesh);
    const ground = crustMaterial(terrain.mesh.material as THREE.MeshStandardMaterial, this.crustU);
    this.mats.push(ground);
    terrain.mesh.material = ground;
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground') {
        tint(o as THREE.Mesh);
        this.level(o as THREE.Mesh);
        (o as THREE.Mesh).material = ground;
      } else if (o.name === 'roads') o.visible = false;
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && (o.name === 'trunks' || o.name === 'cones' || o.name === 'blobs' || o.name === 'tufts' || o.name === 'rocks')) o.visible = false;
    });
    // The world's fog bank at the edge, the flats' own colour.
    if (terrain.fog) terrain.fog.group.visible = false;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.buildMist();
    this.paint(hour);
  }

  /** The world beyond the board laid flat at the flats' height. */
  private level(mesh: THREE.Mesh): void {
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const v = new THREE.Vector3();
    const inv = mesh.matrixWorld.clone().invert();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      v.y = this.flat - 0.01;
      v.applyMatrix4(inv);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    pos.needsUpdate = true;
    mesh.geometry.computeVertexNormals();
  }

  paint(hour: Hour): void {
    this.hour = hour;
    const fogAct = this.place === 'saltFog';
    const look = HOURS[fogAct && hour === 'day' ? 'dusk' : hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog);
      // The haze only takes the horizon: the scene's fog is measured from the camera (58 away on a phone), so a short
      // `near` greyed the whole board (docs/RENDIMIENTO.md, «Las salinas»); the fog that comes and goes is the sheet.
      scene.fog.near = fogAct ? 46 : 62;
      scene.fog.far = fogAct ? 120 : 160;
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.3, 0.55, -0.7);
    if (this.waterMat) {
      (this.waterMat.uniforms.sky as THREE.IUniform<THREE.Color>).value.set(look.sky[1]);
      (this.waterMat.uniforms.night as THREE.IUniform<number>).value = hour === 'night' ? 1 : 0;
    }
    for (const m of this.mists) {
      (m.uniforms.color as THREE.IUniform<THREE.Color>).value.set(look.fog);
      (m.uniforms.thick as THREE.IUniform<number>).value = Math.min(0.95, (fogAct ? 0.85 : this.foggy ? 0.7 : this.lit ? 0.12 : 0.4) * look.mist);
    }
    this.crustU.uGlint.value = hour === 'night' ? 0.25 : 1;
  }

  /* ---------- the place ---------- */

  private buildFlats(kit: Kit, r: SaltRules): void {
    // The board's pools, then the flats' beyond them to the fog (the same grid).
    const own = new Set<string>();
    r.pools.forEach((p, i) => {
      const w = this.ctx.at(p.x, p.y, 0);
      own.add(`${p.x},${p.y}`);
      // (The puzzle's ponds keep near their basin; the board's others are a little freer, never far from the sim's
      // rectangle: what stands in its water is unseen, so the drawn shore can't leave much of it out.)
      const puzzle = (r.gates ?? []).some((g) => g.a === i || g.b === i);
      const h1 = ((i * 2654435761) >>> 0) / 4294967296;
      const h2 = ((i * 40503 + 977) % 1000) / 1000;
      const fx = puzzle ? 0.94 + h1 * 0.06 : 0.84 + h1 * 0.18;
      const fz = puzzle ? 0.94 + h2 * 0.06 : 0.84 + h2 * 0.18;
      this.pools.push({ g: 0, x: w.x + (puzzle ? 0 : (h2 - 0.5) * 0.2), z: w.z + (puzzle ? 0 : (h1 - 0.5) * 0.16), hx: SALT_HALF_X * K, hz: SALT_HALF_Y * K, board: i, shown: -1, tint: (i * 7 + (i >> 2)) % 4, fx, fz });
    });
    // (Beyond the board the ponds are the salt folk's own: of all sizes, a little off the rows, some run together.)
    let ps = 977;
    const prnd = (): number => ((ps = (ps * 1103515245 + 12345) >>> 0) / 4294967296);
    const far = this.ctx.low ? 3 : 5;
    for (let row = -2 - far; row <= 2 + far; row++)
      for (let col = -2 - far; col <= 1 + far; col++) {
        const x = (2 * col + 1) * (SALT_PITCH_X / 2);
        const y = row * SALT_PITCH_Y;
        if (own.has(`${x},${y}`)) continue;
        // The sea's side: past the top rows the flats end at the shore.
        if (row < -4) continue;
        const skip = prnd();
        const sx = 0.55 + prnd() * 0.75;
        const sz = 0.6 + prnd() * 0.65;
        const jx = (prnd() - 0.5) * 900;
        const jy = (prnd() - 0.5) * 700;
        const tint = Math.floor(prnd() * 4);
        if (skip < 0.12) continue;
        const w = this.ctx.at(x + jx, y + jy, 0);
        this.pools.push({ g: 0, x: w.x, z: w.z, hx: Math.min(SALT_PITCH_X / 2 - 250, SALT_HALF_X * sx) * K, hz: Math.min(SALT_PITCH_Y / 2 - 250, SALT_HALF_Y * sz) * K, board: -1, shown: -1, tint });
        // Now and then a small pond in the dyke beside it.
        if (skip > 0.82) {
          const v = this.ctx.at(x + jx + SALT_PITCH_X * 0.48, y + jy + SALT_PITCH_Y * 0.42, 0);
          this.pools.push({ g: 0, x: v.x, z: v.z, hx: 380 * K, hz: 300 * K, board: -1, shown: -1, tint: (tint + 2) % 4 });
        }
      }
    // Beds (instanced), the colour of the crust: the pond's own shape is its water's (irregular, waterMat); no square
    // rims (they read as a tiled floor: the dykes are the crust between the ponds).
    const n = this.pools.length;
    const bedGeo = new THREE.BoxGeometry(1, 1, 1);
    this.geos.push(bedGeo);
    const bedMat = new THREE.MeshStandardMaterial({ roughness: 0.55, metalness: 0.05 });
    this.mats.push(bedMat);
    const beds = new THREE.InstancedMesh(bedGeo, bedMat, n);
    beds.name = 'salt-beds';
    beds.receiveShadow = true;
    const m4 = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const sc = new THREE.Vector3();
    const v = new THREE.Vector3();
    const c = new THREE.Color();
    for (const p of this.pools) p.g = this.flat;
    this.pools.forEach((p, i) => {
      const g = p.g;
      m4.compose(v.set(p.x, g + 0.006, p.z), q, sc.set(p.hx * 2, 0.012, p.hz * 2));
      beds.setMatrixAt(i, m4);
      beds.setColorAt(i, c.set('#e2e6e9'));
    });
    if (beds.instanceColor) beds.instanceColor.needsUpdate = true;
    this.beds = beds;
    this.group.add(beds);
    // The marks: four stakes on each of the puzzle's pools' near rim, white as far as its water reaches.
    const puzzle = new Set<number>();
    for (const g of r.gates ?? []) puzzle.add(g.a).add(g.b);
    const stakeGeo = new THREE.BoxGeometry(0.07, 0.22, 0.07);
    this.geos.push(stakeGeo);
    const stakeMat = new THREE.MeshStandardMaterial({ roughness: 0.6 });
    this.mats.push(stakeMat);
    const list = [...puzzle].sort((a, b) => a - b);
    const stakes = new THREE.InstancedMesh(stakeGeo, stakeMat, Math.max(1, list.length * r.cap));
    stakes.name = 'salt-stakes';
    stakes.castShadow = true;
    list.forEach((pi, j) => {
      const p = this.pools[pi] as PoolView;
      const g = p.g;
      for (let k = 0; k < r.cap; k++) {
        m4.compose(v.set(p.x - p.hx * 0.6 + k * p.hx * 0.4, g + 0.16, p.z + p.hz + 0.06 * S), q, sc.set(1, 1 + k * 0.35, 1));
        stakes.setMatrixAt(j * r.cap + k, m4);
        stakes.setColorAt(j * r.cap + k, c.set('#2a2e35'));
      }
    });
    this.stakes = { mesh: stakes, pools: list };
    this.group.add(stakes);
    // The water (instanced planes, their height the marks).
    const wg = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.geos.push(wg);
    const wm = waterMat();
    this.waterMat = wm;
    this.mats.push(wm);
    const water = new THREE.InstancedMesh(wg, wm, n);
    water.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    water.name = 'salt-water';
    water.renderOrder = 2;
    water.frustumCulled = false;
    this.water = water;
    this.group.add(water);
    // The gate house in the middle: pale stone, slate roof, its door to the coast, two lit windows.
    const hp = this.ctx.at(r.house.x, r.house.y, 0);
    const hy = this.flat;
    const w = 1.15 * S;
    const d = 0.95 * S;
    kit.add(new THREE.BoxGeometry(w, 0.95, d), STONE[0] as string, place(hp.x, hy + 0.475, hp.z));
    kit.add(new THREE.BoxGeometry(w * 1.04, 0.1, d * 1.04), STONE[1] as string, place(hp.x, hy + 0.05, hp.z));
    const roof = new THREE.CylinderGeometry(0.01, 1, 1, 4, 1);
    kit.add(roof, SLATE, place(hp.x, hy + 1.28, hp.z, Math.PI / 4, new THREE.Vector3(w * 0.78, 0.62, d * 0.78)));
    roof.dispose();
    kit.add(new THREE.BoxGeometry(0.18, 0.5, 0.18), STONE[2] as string, place(hp.x + w * 0.3, hy + 1.55, hp.z - d * 0.15));
    this.smokeAt.push(new THREE.Vector3(hp.x + w * 0.3, hy + 1.82, hp.z - d * 0.15));
    const dp = this.ctx.at(r.door.x, r.door.y, 0);
    this.anchors.door = dp.clone();
    kit.add(new THREE.BoxGeometry(0.34, 0.62, 0.06), '#2a2c31', place(hp.x, hy + 0.31, hp.z + d / 2 + 0.01));
    for (const sx of [-1, 1]) {
      kit.add(new THREE.BoxGeometry(0.2, 0.18, 0.06), '#202227', place(hp.x + sx * w * 0.3, hy + 0.6, hp.z + d / 2 + 0.01));
      this.lamps.push(new THREE.Vector3(hp.x + sx * w * 0.3, hy + 0.6, hp.z + d / 2 + 0.06));
    }
    // A salt mound and a rake against the house.
    kit.add(new THREE.ConeGeometry(0.42, 0.5, 9), SALT[0] as string, place(hp.x - w * 0.75, hy + 0.25, hp.z + d * 0.2));
    kit.add(new THREE.BoxGeometry(0.04, 0.04, 0.9), '#6d5f4e', place(hp.x + w * 0.56, hy + 0.35, hp.z + d * 0.4, 0.3, 1, 0.9, 0));
    // The sluices: two posts, a plank in the gap, a small wheel on the crossbar. Its plank rises as it is turned.
    const post = new THREE.BoxGeometry(0.08, 0.5, 0.08);
    const bar = new THREE.BoxGeometry(0.46, 0.06, 0.07);
    const wheel = new THREE.TorusGeometry(0.11, 0.02, 4, 10);
    const plankGeo = new THREE.BoxGeometry(0.34, 0.24, 0.05);
    this.geos.push(plankGeo);
    const plankMat = new THREE.MeshStandardMaterial({ color: '#6f6152', roughness: 0.95, flatShading: true });
    this.mats.push(plankMat);
    for (const g of r.gates ?? []) {
      const a = r.pools[g.a] as { x: number; y: number };
      const b = r.pools[g.b] as { x: number; y: number };
      const at = this.ctx.at(g.x, g.y, 0);
      const gy = this.flat;
      // Across the run of the water (a to b).
      const yaw = Math.atan2(b.x - a.x, b.y - a.y) + Math.PI / 2;
      const ox = Math.cos(yaw) * 0.22;
      const oz = -Math.sin(yaw) * 0.22;
      kit.add(post, '#5a4e42', place(at.x + ox, gy + 0.25, at.z + oz));
      kit.add(post, '#5a4e42', place(at.x - ox, gy + 0.25, at.z - oz));
      kit.add(bar, '#5a4e42', place(at.x, gy + 0.5, at.z, yaw));
      kit.add(wheel, '#4a3f35', place(at.x, gy + 0.62, at.z, yaw));
      kit.add(new THREE.BoxGeometry(0.5, 0.05, 0.3), SALT[1] as string, place(at.x, gy + 0.02, at.z, yaw));
      const plank = new THREE.Mesh(plankGeo, plankMat);
      plank.position.set(at.x, gy + 0.12, at.z);
      plank.rotation.y = yaw;
      plank.castShadow = true;
      this.group.add(plank);
      this.gates.push({ at, plank, yaw, up: 0 });
      this.anchors.gates.push(at.clone().setY(gy + 0.7));
    }
    post.dispose();
    bar.dispose();
    wheel.dispose();
    // The third altar of the first Vigil in the middle of its pool, its eye toward the coast.
    const A = r.pools[r.altar] as { x: number; y: number };
    const ap = this.ctx.at(A.x, A.y, 0);
    this.altar(kit, ap, 0, 1.05);
    this.anchors.altar = ap.clone().setY(ap.y + 1.6);
    // Salt heaps on the dykes here and there (the work of the four houses), and boards to walk on.
    let seed = 41;
    const rnd = (): number => ((seed = (seed * 1103515245 + 12345) >>> 0) / 4294967296);
    const cone = new THREE.ConeGeometry(1, 1, 9);
    for (let i = 0; i < 70; i++) {
      const col = Math.floor(rnd() * 10) - 5;
      const row = Math.floor(rnd() * 9) - 4;
      const x = col * SALT_PITCH_X + (rnd() - 0.5) * 900;
      const y = row * SALT_PITCH_Y + SALT_PITCH_Y / 2 + (rnd() - 0.5) * 200;
      // (Off the board's own dykes near the puzzle and the house: those stay clear to walk.)
      if (Math.abs(x) < 6500 && Math.abs(y) < 5600) continue;
      const p = this.ctx.at(x, y, 0);
      const gy = this.flat;
      // In rows of three or four along the dyke, raked up from the ponds: a big one and its smaller ones.
      const many = 1 + Math.floor(rnd() * 3);
      for (let k = 0; k < many; k++) {
        const hgt = (0.28 + rnd() * 0.42) * (k ? 0.7 : 1);
        kit.add(cone, SALT[(i + k) % 3] as string, place(p.x + k * 0.55, gy + hgt / 2, p.z + (rnd() - 0.5) * 0.12, rnd() * 3, new THREE.Vector3(hgt * 0.95, hgt, hgt * 0.95)));
      }
    }
    cone.dispose();
    // Plank walkways on the dykes: boards end to end, a gap now and then, grey with the salt in them.
    const board = new THREE.BoxGeometry(0.3, 0.025, 0.56);
    const across = new THREE.BoxGeometry(0.56, 0.025, 0.3);
    for (const [cx, cy, len, along] of [
      [-2, -3, 6, 1],
      [1, -1, 5, 0],
      [-3, 2, 6, 0],
      [3, 3, 5, 1],
      [-1, 4, 4, 0],
      [4, -2, 5, 1],
    ] as const) {
      for (let k = 0; k < len * 4; k++) {
        const x = along ? cx * SALT_PITCH_X + k * 485 - len * 970 : cx * SALT_PITCH_X;
        const y = along ? cy * SALT_PITCH_Y + SALT_PITCH_Y / 2 : cy * SALT_PITCH_Y + k * 485 - len * 970;
        if (Math.abs(x) < 6500 && Math.abs(y) < 5600) continue;
        const p = this.ctx.at(x, y, 0);
        kit.add(along ? across : board, k % 3 === 0 ? '#8f8e8b' : k % 2 ? '#a3a2a0' : '#999895', place(p.x, this.flat + 0.015, p.z, (rnd() - 0.5) * 0.06));
      }
    }
    board.dispose();
    across.dispose();
  }

  /** The four houses of the salt by the shore (south, past the board), low stone with slate; drying racks; heaps. */
  private buildShore(kit: Kit): void {
    const spots: [number, number, number][] = [
      [-7200, 10200, 0.2],
      [-3600, 11400, -0.1],
      [1400, 11000, 0.15],
      [5600, 10400, -0.25],
    ];
    for (const [sx, sy, yaw] of spots) {
      const p = this.ctx.at(sx, sy, 0);
      const g = this.flat;
      kit.add(new THREE.BoxGeometry(1.5, 0.8, 1.05), STONE[(sx >> 3) & 1 ? 1 : 0] as string, place(p.x, g + 0.4, p.z, yaw));
      const roof = new THREE.CylinderGeometry(0.01, 1, 1, 4, 1);
      kit.add(roof, SLATE, place(p.x, g + 1.05, p.z, yaw + Math.PI / 4, new THREE.Vector3(1.05, 0.5, 0.75)));
      roof.dispose();
      kit.add(new THREE.BoxGeometry(0.28, 0.48, 0.05), '#2a2c31', place(p.x + Math.sin(yaw) * 0.53, g + 0.24, p.z + Math.cos(yaw) * 0.53, yaw));
      kit.add(new THREE.ConeGeometry(0.5, 0.6, 9), SALT[0] as string, place(p.x + 1.3, g + 0.3, p.z + 0.4));
      this.lamps.push(new THREE.Vector3(p.x + Math.sin(yaw) * 0.55, g + 0.45, p.z + Math.cos(yaw) * 0.55));
      // A chimney, its smoke (smokeAt) drifting off with the wind.
      kit.add(new THREE.BoxGeometry(0.2, 0.55, 0.2), STONE[2] as string, place(p.x - 0.4, g + 1.15, p.z - 0.15, yaw));
      this.smokeAt.push(new THREE.Vector3(p.x - 0.4, g + 1.45, p.z - 0.15));
      // Nets hung to dry between two poles, and a handcart with its load of salt.
      const nx = p.x - 1.4;
      const nz = p.z + 0.5;
      for (const o of [-0.5, 0.5]) kit.add(new THREE.BoxGeometry(0.05, 0.85, 0.05), '#4a4640', place(nx + o, g + 0.42, nz));
      kit.add(new THREE.BoxGeometry(1.0, 0.03, 0.03), '#4a4640', place(nx, g + 0.83, nz));
      kit.add(new THREE.BoxGeometry(0.95, 0.42, 0.01), '#59606a', place(nx, g + 0.6, nz, 0, 1, 0.06, 0));
      for (let f = 0; f < 4; f++) kit.add(new THREE.BoxGeometry(0.07, 0.05, 0.05), '#d3d8de', place(nx - 0.36 + f * 0.24, g + 0.8, nz));
      const cx = p.x + 0.9;
      const cz = p.z + 1.1;
      kit.add(new THREE.BoxGeometry(0.62, 0.22, 0.4), '#56514b', place(cx, g + 0.28, cz, yaw + 0.4));
      kit.add(new THREE.ConeGeometry(0.26, 0.2, 8), SALT[1] as string, place(cx, g + 0.48, cz, yaw));
      for (const o of [-1, 1]) kit.add(new THREE.TorusGeometry(0.14, 0.03, 4, 10), '#3a362f', place(cx, g + 0.15, cz + o * 0.23, Math.PI / 2));
      kit.add(new THREE.BoxGeometry(0.04, 0.04, 0.55), '#4a4640', place(cx - 0.45, g + 0.3, cz, yaw + 0.4, 1, 0, 0.3));
    }
  }

  /** An altar of the first Vigil (chapter 1's shape): pale stone on a plinth, its eye high on its face. */
  private altar(kit: Kit, at: THREE.Vector3, face: number, scale: number): void {
    const plinth = new THREE.CylinderGeometry(0.42 * scale, 0.48 * scale, 0.14 * scale, 6);
    const stele = new THREE.BoxGeometry(0.5 * scale, 0.98 * scale, 0.24 * scale);
    const top = new THREE.ConeGeometry(0.36 * scale, 0.26 * scale, 4);
    kit.add(plinth, '#8a9099', place(at.x, at.y + 0.07 * scale, at.z, face + Math.PI / 6));
    kit.add(stele, '#c3c8cf', place(at.x, at.y + 0.63 * scale, at.z, face));
    kit.add(top, '#cfd3d9', place(at.x, at.y + 1.25 * scale, at.z, face + Math.PI / 4, new THREE.Vector3(1, 1, 0.48)));
    // The water's line on it: a darker band where the pool kept it, and salt crusted above.
    kit.add(new THREE.BoxGeometry(0.52 * scale, 0.3 * scale, 0.26 * scale), '#7f8892', place(at.x, at.y + 0.3 * scale, at.z, face));
    kit.add(new THREE.BoxGeometry(0.53 * scale, 0.04 * scale, 0.27 * scale), '#f4f6f8', place(at.x, at.y + 0.47 * scale, at.z, face));
    plinth.dispose();
    stele.dispose();
    top.dispose();
    const fx = Math.sin(face);
    const fz = Math.cos(face);
    const eyeAt = new THREE.Vector3(at.x + fx * 0.128 * scale, at.y + 0.86 * scale, at.z + fz * 0.128 * scale);
    const lid = new THREE.TorusGeometry(0.16 * scale, 0.028 * scale, 4, 18);
    lid.scale(1, 0.5, 1);
    kit.add(lid, '#3a3d45', place(eyeAt.x, eyeAt.y, eyeAt.z, face));
    lid.dispose();
    const mat = new THREE.MeshStandardMaterial({ color: '#1c1d22', emissive: new THREE.Color('#000000'), roughness: 0.4 });
    this.mats.push(mat);
    const geo = new THREE.CircleGeometry(0.068 * scale, 16);
    this.geos.push(geo);
    const iris = new THREE.Mesh(geo, mat);
    iris.position.set(eyeAt.x + fx * 0.006, eyeAt.y, eyeAt.z + fz * 0.006);
    iris.rotation.y = face;
    this.group.add(iris);
    this.eye = { at: eyeAt, mesh: iris, mat, open: 0 };
  }

  private buildMist(): void {
    const g = new THREE.PlaneGeometry(260, 260, 1, 1).rotateX(-Math.PI / 2);
    this.geos.push(g);
    // Three sheets, one over the other (on a slow phone, two): the fog has a body, not a floor's film.
    const layers = this.ctx.low ? 2 : 3;
    for (let k = 0; k < layers; k++) {
      const m = mistMat(k);
      if (k === 0) this.mistU = m;
      this.mists.push(m);
      this.mats.push(m);
      const mesh = new THREE.Mesh(g, m);
      mesh.position.y = this.flat + 0.45 + k * 0.75;
      mesh.renderOrder = 6 + k;
      mesh.frustumCulled = false;
      mesh.name = 'salt-mist';
      if (k === 0) this.mist = mesh;
      this.group.add(mesh);
    }
  }

  private buildDecals(): void {
    // One quad, many uses: a soft disc texture drawn by the shader per kind (instance colour's alpha in .r of a 2nd attr).
    const q = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.geos.push(q);
    const kindAttr = new THREE.InstancedBufferAttribute(new Float32Array(128), 1);
    const alphaAttr = new THREE.InstancedBufferAttribute(new Float32Array(128), 1);
    q.setAttribute('aKind', kindAttr);
    q.setAttribute('aAlpha', alphaAttr);
    const m = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -6,
      polygonOffsetUnits: -6,
      uniforms: { time: { value: 0 } },
      vertexShader: /* glsl */ `
        attribute float aKind;
        attribute float aAlpha;
        varying float vKind;
        varying float vAlpha;
        varying vec2 vUv;
        varying vec3 vC;
        void main() {
          vKind = aKind; vAlpha = aAlpha; vUv = uv;
          vC = instanceColor;
          gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float time;
        varying float vKind;
        varying float vAlpha;
        varying vec2 vUv;
        varying vec3 vC;
        float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        void main() {
          vec2 p = vUv - 0.5;
          float r = length(p) * 2.0;
          float a = 0.0;
          if (vKind < 0.5) {
            // A print: two dark ovals (a foot, a foot), toes forward.
            vec2 a1 = (p - vec2(-0.16, 0.05)) * vec2(3.2, 1.9);
            vec2 a2 = (p - vec2(0.16, -0.12)) * vec2(3.2, 1.9);
            a = (1.0 - smoothstep(0.32, 0.42, length(a1))) + (1.0 - smoothstep(0.32, 0.42, length(a2)));
          } else if (vKind < 1.5) {
            // Salt thrown: a disc of grains, thicker at its middle, a ragged rim that sparkles.
            float g = h(floor(vUv * 48.0));
            float edge = 1.0 - smoothstep(0.78 + 0.12 * g, 1.0, r);
            a = edge * (0.55 + 0.45 * g) * (0.8 + 0.2 * (1.0 - r));
            a += step(0.97, g) * edge * (0.5 + 0.5 * sin(time * 5.0 + g * 40.0));
          } else {
            // A mark: a ring that pulses (the leap's spot, red).
            float ring = 1.0 - smoothstep(0.0, 0.12, abs(r - 0.82));
            a = ring + (1.0 - smoothstep(0.0, 0.82, r)) * 0.28;
            a *= 0.7 + 0.3 * sin(time * 12.0);
          }
          gl_FragColor = vec4(vC, clamp(a, 0.0, 1.0) * vAlpha);
          #include <colorspace_fragment>
        }`,
    });
    this.mats.push(m);
    const im = new THREE.InstancedMesh(q, m, 128);
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(128 * 3), 3);
    im.count = 0;
    im.frustumCulled = false;
    im.renderOrder = 5;
    im.name = 'salt-decals';
    this.decals = im;
    this.group.add(im);
    // The water running through a gate: a white rush along its way.
    const rq = new THREE.PlaneGeometry(1, 1, 1, 1).rotateX(-Math.PI / 2);
    this.geos.push(rq);
    const rm = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { time: { value: 0 } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */ `
        uniform float time;
        varying vec2 vUv;
        void main() {
          float across = 1.0 - abs(vUv.y - 0.5) * 2.0;
          float run = 0.5 + 0.5 * sin(vUv.x * 30.0 - time * 14.0 + vUv.y * 6.0);
          float ends = smoothstep(0.0, 0.2, vUv.x) * (1.0 - smoothstep(0.8, 1.0, vUv.x));
          float a = smoothstep(0.0, 0.5, across) * ends * (0.55 + 0.45 * run);
          vec3 c = mix(vec3(0.78, 0.86, 0.92), vec3(1.0), run);
          gl_FragColor = vec4(c, a * 0.95);
          #include <colorspace_fragment>
        }`,
    });
    this.mats.push(rm);
    const runs = new THREE.InstancedMesh(rq, rm, 8);
    runs.count = 0;
    runs.frustumCulled = false;
    runs.renderOrder = 4;
    runs.name = 'salt-runs';
    this.runs = runs;
    this.group.add(runs);
  }

  /** The campfire's scene: a fire there (its light a glow). */
  setFire(p: THREE.Vector3 | null): void {
    this.fire = p;
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    if (s.tick === this.lastTick) return;
    this.lastTick = s.tick;
    // Prints where the stalkers tread on salt, a pair a step.
    if (s.tick % 3 !== 0) return;
    for (const e of s.enemies) {
      if (e.hp <= 0 || e.kind !== 'stalker') continue;
      const last = this.printLast.get(e.id);
      if (last && Math.abs(last.x - e.x) + Math.abs(last.y - e.y) < 520) continue;
      this.printLast.set(e.id, { x: e.x, y: e.y });
      if (!saltGround(s, e.x, e.y)) continue;
      const p = this.ctx.at(e.x, e.y, 0);
      const yaw = last ? Math.atan2(e.x - last.x, e.y - last.y) : 0;
      this.prints.push({ x: p.x, z: p.z, yaw, t: this.now });
      if (this.prints.length > 90) this.prints.shift();
    }
  }

  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private up = new THREE.Vector3(0, 1, 0);

  draw(s: BattleState): void {
    const r = s.rules?.senda?.salt;
    const w = s.senda?.salt;
    if (!r) return;
    const level = w?.level ?? r.start;
    // The marks a pool will have when the water now running through a gate has run (the steps ease toward it).
    const want = level.slice();
    const flowK = new Map<number, number>();
    if (w && r.gates && r.sluice) {
      const lv = level.slice();
      for (const f of w.flows) {
        const g = r.gates[f.g];
        if (!g) continue;
        const [na, nb] = sluiceFlow(g.down, lv[g.a] as number, lv[g.b] as number, r.cap);
        lv[g.a] = na;
        lv[g.b] = nb;
        const k = 1 - Math.max(0, f.end - s.tick) / Math.max(1, r.sluice.flow);
        flowK.set(f.g, k);
        want[g.a] = (level[g.a] as number) + (na - (level[g.a] as number)) * k;
        want[g.b] = (level[g.b] as number) + (nb - (level[g.b] as number)) * k;
      }
    }
    const water = this.water;
    const beds = this.beds;
    if (water && beds) {
      let n = 0;
      this.pools.forEach((p, i) => {
        const marks = p.board < 0 ? r.cap : this.lit && p.board === r.altar ? 0 : (want[p.board] as number);
        p.shown = p.shown < 0 ? marks : p.shown + (marks - p.shown) * 0.25;
        const g = p.g;
        const mud = p.board === r.altar && r.kind === 'hold';
        const dry = p.shown < 0.04;
        // The bed shows only as the altar's fresh mud: a pond is its water's outline, a dry one is crust like the dykes
        // (a square bed under every pond read as tiles).
        beds.setColorAt(i, this.c3.set('#5d5a58'));
        this.m4.compose(this.v3.set(p.x, g + 0.006, p.z), this.q4.identity(), this.s3.set(dry && mud ? p.hx * 2 * (p.fx ?? 1) : 0, dry && mud ? 0.012 : 0, dry && mud ? p.hz * 2 * (p.fz ?? 1) : 0));
        beds.setMatrixAt(i, this.m4);
        if (dry) return;
        // Its level as it reads from above: full to the rims; lower, the wet bed shows round it (a mark a band).
        const k = 0.5 + 0.5 * (p.shown / r.cap);
        this.m4.compose(this.v3.set(p.x, g + 0.012 + (p.shown / r.cap) * 0.075, p.z), this.q4.identity(), this.s3.set(p.hx * 2 * k * (p.fx ?? 1), 1, p.hz * 2 * k * (p.fz ?? 1)));
        water.setColorAt(n, TINTS[p.tint] as THREE.Color);
        water.setMatrixAt(n++, this.m4);
      });
      water.count = n;
      water.instanceMatrix.needsUpdate = true;
      if (water.instanceColor) water.instanceColor.needsUpdate = true;
      if (beds.instanceColor) beds.instanceColor.needsUpdate = true;
      beds.instanceMatrix.needsUpdate = true;
    }
    if (this.stakes) {
      const st = this.stakes;
      st.pools.forEach((pi, j) => {
        const shown = (this.pools[pi] as PoolView).shown;
        for (let k = 0; k < r.cap; k++) st.mesh.setColorAt(j * r.cap + k, this.c3.set(k < Math.round(shown) ? '#eaf2f8' : '#2a2e35'));
      });
      if (st.mesh.instanceColor) st.mesh.instanceColor.needsUpdate = true;
    }
    // The sluices: a plank up while it is turned or locked; a rush of water while it runs.
    const runs = this.runs;
    if (runs) {
      let k = 0;
      this.gates.forEach((g, i) => {
        const locked = !!w && (w.locked[i] ?? 0) === 1;
        const turning = !!w && w.job === i && w.held > 0;
        const target = locked ? 1 : turning ? Math.min(1, w.held / Math.max(1, r.sluice?.turn ?? 1)) * 0.6 : 0;
        g.up += (target - g.up) * 0.3;
        g.plank.position.y = this.flat + 0.12 + g.up * 0.3;
        const f = flowK.get(i);
        if (f === undefined || !r.gates) return;
        const gate = r.gates[i] as NonNullable<SaltRules['gates']>[number];
        const a = r.pools[gate.a] as { x: number; y: number };
        const b = r.pools[gate.b] as { x: number; y: number };
        const pa = this.ctx.at(a.x, a.y, 0);
        const pb = this.ctx.at(b.x, b.y, 0);
        const len = pa.distanceTo(pb) * 0.42;
        this.q4.setFromAxisAngle(this.up, -Math.atan2(pb.z - pa.z, pb.x - pa.x));
        this.m4.compose(this.v3.set(g.at.x, this.flat + 0.11, g.at.z), this.q4, this.s3.set(len, 1, 0.34 * (1 - f * 0.5)));
        runs.setMatrixAt(k++, this.m4);
      });
      runs.count = k;
      runs.instanceMatrix.needsUpdate = true;
    }
    // The altar's eye: open with its pool drained (the gates), kindling with the clock (the test), open in the close.
    if (this.eye) {
      let open = 0;
      if (this.lit) open = 1;
      else if (r.kind === 'sluice') open = w && w.dried >= 0 ? 1 : 0;
      else if (r.kind === 'hold') open = s.result === 'won' ? 1 : Math.min(1, s.waveT / Math.max(1, s.rules?.senda?.goal.need ?? 1));
      this.eye.open += (open - this.eye.open) * 0.08;
    }
    this.drawDecals(s, r, w);
  }

  private drawDecals(s: BattleState, r: SaltRules, w: NonNullable<BattleState['senda']>['salt']): void {
    const d = this.decals;
    if (!d) return;
    const kind = d.geometry.getAttribute('aKind') as THREE.InstancedBufferAttribute;
    const alpha = d.geometry.getAttribute('aAlpha') as THREE.InstancedBufferAttribute;
    let n = 0;
    const put = (x: number, z: number, y: number, size: number, yaw: number, k: number, a: number, col: THREE.Color): void => {
      if (n >= 128) return;
      this.q4.setFromAxisAngle(this.up, yaw);
      this.m4.compose(this.v3.set(x, y, z), this.q4, this.s3.set(size, 1, size));
      d.setMatrixAt(n, this.m4);
      d.setColorAt(n, col);
      kind.setX(n, k);
      alpha.setX(n, a);
      n++;
    };
    const dark = this.c3.set('#4a5560').clone();
    for (const p of this.prints) {
      const age = this.now - p.t;
      if (age > 7) continue;
      put(p.x, p.z, this.flat + 0.02, 0.42, p.yaw, 0, Math.min(1, 1.6 - age / 4.5) * 0.85, dark);
    }
    if (w && r.hold) {
      const white = new THREE.Color('#ffffff');
      const grey = new THREE.Color('#b9c0c7');
      for (const c of w.circles) {
        const left = c.until - s.tick;
        const p = this.ctx.at(c.x, c.y, 0);
        const total = c.wet ? r.hold.wet : r.hold.last;
        const k = Math.max(0, Math.min(1, left / Math.max(1, total)));
        const size = r.hold.radius * K * 2 * (c.wet ? 0.6 + 0.4 * k : 1);
        put(p.x, p.z, this.flat + 0.1, size, c.x * 0.001, 1, (c.wet ? 0.85 * k : 0.55 + 0.45 * Math.min(1, k * 3)), c.wet ? grey : white);
      }
      if (w.throwing) {
        // The salt in the air: a puff that flies from Marit to its spot (drawn at the spot, growing as it lands).
        const p = this.ctx.at(w.throwing.x, w.throwing.y, 0);
        const left = Math.max(0, w.throwing.at - s.tick) / Math.max(1, r.hold.throw);
        put(p.x, p.z, this.flat + 0.1, r.hold.radius * K * 2 * (1 - left) * 0.8, 0, 1, 0.5, white);
      }
      if (w.old.id && w.old.mode === 1) {
        const p = this.ctx.at(w.old.x, w.old.y, 0);
        put(p.x, p.z, this.flat + 0.12, r.hold.old.cover * K * 2.2, 0, 2, 1, RED);
      }
    }
    d.count = n;
    d.instanceMatrix.needsUpdate = true;
    if (d.instanceColor) d.instanceColor.needsUpdate = true;
    kind.needsUpdate = true;
    alpha.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    this.now = now;
    this.dome?.frame(now, camera);
    this.crustU.uTime.value = now;
    for (const m of [this.waterMat, ...this.mists, this.smoke?.material as THREE.ShaderMaterial | undefined, this.decals?.material as THREE.ShaderMaterial | undefined, this.runs?.material as THREE.ShaderMaterial | undefined]) {
      const t = m?.uniforms?.time as THREE.IUniform<number> | undefined;
      if (t) t.value = now;
    }
    this.glow.begin();
    // The gate house's windows and the four houses' doors, lit at dusk and night.
    const lit = this.hour === 'night' ? 1 : this.place === 'saltFog' ? 0.6 : 0;
    if (lit > 0)
      this.lamps.forEach((l, i) => {
        const f = 0.85 + 0.15 * Math.sin(now * (4 + i) + i);
        this.glow.add(l.x, l.y, l.z, LAMP, 1.1, 0.7 * f * lit);
      });
    if (this.fire) {
      const f = 0.8 + 0.2 * Math.sin(now * 9) * Math.sin(now * 5.3);
      this.glow.add(this.fire.x, this.fire.y + 0.25, this.fire.z, new THREE.Color('#ffb070'), 3.4, 0.65 * f);
    }
    if (this.eye) {
      const k = this.eye.open;
      this.eye.mat.emissive.copy(RED).lerp(WHITE, 0.55).multiplyScalar(k * 1.6);
      this.eye.mat.color.set('#1c1d22').lerp(WHITE, k);
      if (k > 0.03) {
        const e = this.eye.at;
        this.glow.add(e.x, e.y, e.z, RED_SOFT, 0.9 + 0.6 * k, 0.5 * k);
        this.glow.add(e.x, e.y, e.z, WHITE, 0.5 + 0.2 * k, 0.65 * k);
      }
    }
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
